/**
 * Injects the popup that previews an analytical variable.
 *
 * The popup (`AvDenovoPreviewModal`) lives in `feature-analytical-variables`.
 * `feature-analysis` cannot import that lib: it imports `feature-epi-chat`,
 * which imports `analysisKeys` / `useAnalysisProfileQuery` from
 * `feature-analysis`, so the import would close the cycle
 * feature-analysis → feature-analytical-variables → feature-epi-chat →
 * feature-analysis (rejected by `@nx/enforce-module-boundaries`). Instead the
 * host that mounts the analysis pages and already imports both libs
 * (`ui-routes`) supplies the renderer through this context — the same
 * injection approach as `resourceResolvers` in `useEpiChatPage`.
 *
 * `null` (default) → table section headers that reference a variable render
 * as plain text instead of a link.
 */
import { createContext, useContext, type ReactNode } from 'react';

import type { VariablePreviewRenderer } from '../types';

const VariablePreviewContext = createContext<VariablePreviewRenderer | null>(null);

interface VariablePreviewProviderProps {
  renderPreview: VariablePreviewRenderer | null;
  children: ReactNode;
}

export const VariablePreviewProvider = ({
  renderPreview,
  children,
}: VariablePreviewProviderProps) => (
  <VariablePreviewContext.Provider value={renderPreview}>
    {children}
  </VariablePreviewContext.Provider>
);

export const useVariablePreviewRenderer = (): VariablePreviewRenderer | null =>
  useContext(VariablePreviewContext);
